import { useEffect, type JSX } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import { formatFileSize } from '@footsteps/shared';
import { useAttachmentItemViewModel } from './attachment-item.viewmodel';
import { useAttachmentImageViewModel } from './attachment-image.viewmodel';
import type { AttachmentImageProps, AttachmentItemProps, AttachmentListProps } from './attachment.types';

const AttachmentImage = ({ attachment, onGetSignedUrl }: AttachmentImageProps): JSX.Element => {
  const [state, viewModel] = useAttachmentImageViewModel(attachment.id, onGetSignedUrl);

  useEffect(() => {
    viewModel.load();
  }, [viewModel]);

  const thumbnail = (
    <div className='attachment-file-icon attachment-image-icon'>
      {state.blobUrl ? (
        <img
          src={state.blobUrl}
          alt={attachment.description ?? attachment.filename}
          className='attachment-image-thumb'
          onError={viewModel.markFailed}
        />
      ) : state.failed ? (
        <InsertDriveFileOutlinedIcon />
      ) : (
        <ImageOutlinedIcon />
      )}
    </div>
  );

  if (!state.blobUrl) return thumbnail;

  return (
    <Tooltip
      title={
        <img
          src={state.blobUrl}
          alt={attachment.description ?? attachment.filename}
          className='attachment-image-tooltip'
        />
      }
      placement='right'
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      slotProps={{
        tooltip: { className: 'attachment-image-tooltip-container' },
      }}
    >
      {thumbnail}
    </Tooltip>
  );
};

const AttachmentItem = ({
  attachment,
  disabled,
  onDelete,
  onGetSignedUrl,
  onUpdateDescription,
}: AttachmentItemProps): JSX.Element => {
  const [state, viewModel] = useAttachmentItemViewModel(attachment, { onDelete, onGetSignedUrl, onUpdateDescription });

  return (
    <>
      <div className='attachment-item'>
        {viewModel.isImage ? (
          <AttachmentImage attachment={attachment} onGetSignedUrl={onGetSignedUrl} />
        ) : (
          <div className='attachment-file-icon'>
            <InsertDriveFileOutlinedIcon />
          </div>
        )}

        <div className='attachment-item-info'>
          <span className='attachment-item-name' title={attachment.filename}>
            {attachment.filename}
          </span>
          <span className='attachment-item-meta'>
            {formatFileSize(attachment.sizeBytes)}
            {viewModel.imageDimensions}
          </span>

          {state.isEditingDescription ? (
            <div className='attachment-description-edit'>
              <TextField
                size='small'
                value={state.descriptionValue}
                onChange={(event) => viewModel.setDescriptionValue(event.target.value)}
                placeholder='Beschrijving toevoegen...'
                fullWidth
                autoFocus
                onKeyDown={viewModel.handleDescriptionKeyDown}
              />
              <IconButton size='small' onClick={viewModel.saveDescription}>
                <CheckIcon fontSize='small' />
              </IconButton>
              <IconButton size='small' onClick={viewModel.cancelEditDescription}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </div>
          ) : (
            <span
              className='attachment-item-description'
              onClick={!disabled && viewModel.canEditDescription ? viewModel.startEditDescription : undefined}
              role={!disabled && viewModel.canEditDescription ? 'button' : undefined}
              tabIndex={!disabled && viewModel.canEditDescription ? 0 : undefined}
            >
              {attachment.description ||
                (!disabled && viewModel.canEditDescription ? (
                  <span className='attachment-description-placeholder'>
                    <EditIcon sx={{ fontSize: 14 }} /> Beschrijving toevoegen
                  </span>
                ) : null)}
            </span>
          )}
        </div>

        <div className='attachment-item-actions'>
          <IconButton size='small' onClick={viewModel.download} title='Downloaden'>
            <DownloadIcon fontSize='small' />
          </IconButton>
          {!disabled && (
            <IconButton size='small' onClick={viewModel.openDeleteConfirm} title='Verwijderen'>
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          )}
        </div>
      </div>

      <Dialog open={state.deleteConfirmOpen} onClose={viewModel.closeDeleteConfirm}>
        <DialogTitle>Bijlage verwijderen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Weet je zeker dat je &quot;{attachment.filename}&quot; wilt verwijderen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={viewModel.closeDeleteConfirm}>Annuleren</Button>
          <Button onClick={viewModel.confirmDelete} color='error' variant='contained'>
            Verwijderen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const AttachmentList = ({
  attachments,
  isLoading,
  disabled = false,
  onDelete,
  onGetSignedUrl,
  onUpdateDescription,
}: AttachmentListProps): JSX.Element => {
  if (isLoading) {
    return (
      <div className='attachment-list'>
        <Skeleton variant='rectangular' height={64} />
        <Skeleton variant='rectangular' height={64} />
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className='attachment-list-empty'>
        <InsertDriveFileOutlinedIcon className='attachment-list-empty-icon' />
        <span>Nog geen bijlagen</span>
      </div>
    );
  }

  return (
    <div className='attachment-list'>
      {attachments.map((attachment) => (
        <AttachmentItem
          key={attachment.id}
          attachment={attachment}
          disabled={disabled}
          onDelete={onDelete}
          onGetSignedUrl={onGetSignedUrl}
          onUpdateDescription={onUpdateDescription}
        />
      ))}
    </div>
  );
};
